import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

import type { QueryResult } from '../types';

type CurrentUser = {
  __typename?: string;
  id: string;
  bannerMessage: string | null | undefined;
  hasSeenNotifications: {
    jobsCta: boolean;
  };
};

type Offer = {
  id: string;
  acceptedAt: string | null;
  expiry: string;
};

type Plan = {
  id: string;
  interval: 'month' | 'year';
  isInPlanGroup: boolean;
  amount: number;
  name: string;
  type: string;
};

type OverdueInvoice = {
  id: string;
};

type CurrentAccount = {
  __typename?: string;
  id: number;
  currency: string;
  currentUserRole: 'owner' | 'admin' | 'member' | 'super_admin';
  firstKnownUpgradeDate: string | null;
  isUnlimited: boolean;
  // OverdueInvoice
  overdueInvoice: OverdueInvoice | null;
  periodEndDate: string | null;
  // Basic trialing data
  isTrialing: boolean;
  trialEndDate: string | null;
  // Teams data
  isTeamAccount: boolean;
  isTrialingTeams: boolean;
  teamAccountEndDate: string | null;
  teamsTrialAutoUpgrade: boolean;
  teamTrialEndDate: string | null;
  // Companies data
  isCompanyAccount: boolean;
  isTrialingCompanies: boolean;
  companiesTrialEndDate: string | null;
  companiesTrialAutoUpgrade: boolean | null;
  // Current plan data
  plan: Plan;
  // Offers for a given campaign data
  campaignOffers: Offer[];
};

type ResponseData = {
  isAuthenticated: boolean;
  currentUser: CurrentUser;
  currentAccount: CurrentAccount;
  possiblePlans: Array<{
    planType: string;
    plans: Array<Plan>;
  }>;
  nextPlan: Plan;
  nextTeamPlan: Plan;
  nextCompanyPlan: Plan;
};

export type Result = QueryResult<ResponseData>;

export const alertBannerNotificationsQuery: DocumentNode = gql`
  query alertBannerNotifications($accountId: Int!) {
    isAuthenticated: isAuthenticatedV2

    currentUser: currentUserV2 {
      id
      bannerMessage
      hasSeenNotifications {
        jobsCta
      }
    }

    currentAccount: account(id: $accountId) {
      # Core account data
      id
      currency
      currentUserRole
      firstKnownUpgradeDate
      isUnlimited
      periodEndDate
      # Basic trialing data
      isTrialing
      trialEndDate
      # Teams data
      isTeamAccount
      isTrialingTeams
      teamAccountEndDate
      teamsTrialAutoUpgrade
      teamTrialEndDate
      # Companies data
      isCompanyAccount
      isTrialingCompanies
      companiesTrialEndDate
      companiesTrialAutoUpgrade
      # Current plan data
      plan: planV2 {
        id
        name
        interval
        isInPlanGroup
        amount
        type
      }
      # Offers issued to this account for Sunset campaign
      campaignOffers: campaignOffers(campaignSlug: "notion-discount") {
        id
        acceptedAt
        expiry
      }
      # Request any overdue invoices
      overdueInvoice {
        id
      }
    }

    possiblePlans: pricingPageV3Plans(accountId: $accountId) {
      planType
      plans {
        id
        name
        interval
        amount
        type
      }
    }

    # @ref ul-1: Professional* components expects interval to be year,
    # so be cognizant of that if changing.
    nextPlan: nextPlanV2(accountId: $accountId, interval: year) {
      id
      name
      interval
      amount
      type
    }

    nextTeamPlan: nextPlanV2(accountId: $accountId, type: "Team") {
      id
      name
      interval
      amount
      type
    }

    nextCompanyPlan: nextPlanV2(
      accountId: $accountId
      type: "Company"
      interval: year
    ) {
      id
      name
      interval
      amount
      type
    }
  }
`;
